import { CurrencyPipe, NgClass } from '@angular/common';
import { Component, EventEmitter, inject, Input, Output } from '@angular/core';
import { Button } from 'primeng/button';
import { Tooltip } from 'primeng/tooltip';
import { CheckboxModule } from 'primeng/checkbox';
import { FormsModule } from '@angular/forms';
import { DragDropModule } from 'primeng/dragdrop';
import { Popover } from 'primeng/popover';
import { ConfirmationService } from 'primeng/api';
import { ConfirmPopup } from 'primeng/confirmpopup';
import { TagModule } from 'primeng/tag';
import { QuoteRevisionItem } from '../../../models/quote-revision-item.model';
import { QuantityMode } from '../../../models/quote-item-data.model';
import { QuoteRevisionItemService } from '../../services/quote-revision-item.service';
import { DFMCheckStatus } from '../../../models/dfm-check.model';
import { DFMCheckType } from '../../../models/dfm-check.model';
import { DFM_CHECK_DEFINITIONS } from '../../../models/dfm-check-definitions';
import { QuoteItemIndexPipe } from '../../../pipes/quote-item-index.pipe';
import { QuantityPipe } from '../../../pipes/quantity.pipe';
import { QuoteRevisionItemDetailsComponent } from './quote-revision-item-details/quote-revision-item-details.component';

@Component({
  selector: 'app-quote-revision-item',
  // Each row owns a ConfirmPopup. A page-scoped service broadcasts a delete
  // confirmation to every row popup, unlike the legacy item-list behaviour.
  providers: [ConfirmationService],
  imports: [
    Button,
    CheckboxModule,
    ConfirmPopup,
    CurrencyPipe,
    DragDropModule,
    FormsModule,
    NgClass,
    Popover,
    QuantityPipe,
    QuoteItemIndexPipe,
    QuoteRevisionItemDetailsComponent,
    TagModule,
    Tooltip,
  ],
  templateUrl: './quote-revision-item.component.html',
  styleUrl: './quote-revision-item.component.scss',
})
export class QuoteRevisionItemComponent {
  @Input({ required: true }) item!: QuoteRevisionItem;
  @Input({ required: true }) currencyCode!: string;
  @Input() tradeStudyToolFeaturesEnabled = false;
  @Input() consumablesEnabled = false;
  @Input() readOnly = false;
  @Input() selectedPersistentIds: ReadonlySet<string> = new Set();
  @Input() visibleInPdf = true;
  @Output() itemSelectionChange = new EventEmitter<{
    persistentId: string;
    selected: boolean;
  }>();
  @Output() addItemRequested = new EventEmitter<string>();

  protected readonly QuantityMode = QuantityMode;
  revisionItemService = inject(QuoteRevisionItemService);
  confirmationService = inject(ConfirmationService);
  expanded = false;
  detailsVisible = false;
  detailsPanel:
    | 'details'
    | 'properties'
    | 'dfm'
    | 'pricing'
    | 'drawing'
    | 'capex'
    | 'consumables' = 'details';

  get isPriceOverridden(): boolean {
    return (
      this.item.unit_cost_override != null ||
      this.item.quantity_override != null
    );
  }

  get hasCadObjectsForPricing(): boolean {
    const hasGeometry =
      this.item.cad_objects.length > 0 ||
      this.item.inherited_cad_objects.length > 0 ||
      this.item.drawing_files.length > 0 ||
      this.item.inherited_drawing_files.length > 0;
    return hasGeometry && (this.item.children?.length ?? 0) === 0;
  }

  get canOpenPricingDetails(): boolean {
    return this.hasCadObjectsForPricing && this.item.pricing_model_id !== null;
  }

  get linkedCapexEquipmentCount(): number {
    return this.item.equipment_links.length;
  }

  get linkedCapexEquipmentName(): string | undefined {
    return this.item.equipment_links[0]?.equipment.name;
  }

  get linkedConsumableCount(): number {
    return this.item.consumable_links.length;
  }

  get itemListRowClass(): string {
    const columns =
      this.tradeStudyToolFeaturesEnabled && this.consumablesEnabled
        ? 'grid-cols-[80px_repeat(8,minmax(0,1fr))_minmax(8.25rem,1fr)_repeat(4,minmax(0,1fr))]'
        : this.tradeStudyToolFeaturesEnabled
          ? 'grid-cols-[80px_repeat(12,minmax(0,1fr))]'
          : this.consumablesEnabled
            ? 'grid-cols-[80px_repeat(9,minmax(0,1fr))_minmax(8.25rem,1fr)_repeat(4,minmax(0,1fr))]'
            : 'grid-cols-[80px_repeat(13,minmax(0,1fr))]';
    return `item-list-row group grid ${columns} gap-2 items-stretch text-surface-switch-900 cursor-pointer`;
  }

  get itemListBodyClass(): string {
    const span =
      this.tradeStudyToolFeaturesEnabled && this.consumablesEnabled
        ? 'col-span-13'
        : this.tradeStudyToolFeaturesEnabled
          ? 'col-span-12'
          : this.consumablesEnabled
            ? 'col-span-14'
            : 'col-span-13';
    return `${span} grid grid-cols-subgrid items-stretch overflow-visible bg-inherit pl-0 pr-2`;
  }

  get dfmState(): {
    completed: boolean;
    inProgress: boolean;
    failed: boolean;
    warnings: Array<{ cadObjectName: string; checkLabels: string[] }>;
  } {
    let hasRelatedDfmChecks = false;
    let allRelatedDfmChecksCompleted = true;
    let inProgress = false;
    let failed = false;
    const warnings: Array<{ cadObjectName: string; checkLabels: string[] }> =
      [];

    for (const cadObject of [
      ...(this.item.cad_objects.length > 0
        ? this.item.cad_objects
        : this.item.inherited_cad_objects),
    ]) {
      const checks = this.item.dfm_checks.filter(
        (check) => check.cad_object_id === cadObject.id,
      );
      const warningTypes = new Set<DFMCheckType>();

      for (const check of checks) {
        hasRelatedDfmChecks = true;
        if (check.status === DFMCheckStatus.FAILED) {
          failed = true;
        } else if (check.status !== DFMCheckStatus.COMPLETED) {
          inProgress = true;
        }
        if (check.status !== DFMCheckStatus.COMPLETED) {
          allRelatedDfmChecksCompleted = false;
        }
        if (
          DFM_CHECK_DEFINITIONS[check.check_type].isWarningType &&
          parseFloat(check.quantity || '0') > 0
        ) {
          warningTypes.add(check.check_type);
        }
      }

      if (warningTypes.size > 0) {
        warnings.push({
          cadObjectName: cadObject.name,
          checkLabels: [...warningTypes].map(
            (checkType) => DFM_CHECK_DEFINITIONS[checkType].label,
          ),
        });
      }
    }

    return {
      completed: hasRelatedDfmChecks && allRelatedDfmChecksCompleted,
      inProgress,
      failed,
      warnings,
    };
  }

  get hasCompletedDfmChecks(): boolean {
    return this.dfmState.completed;
  }

  get hasDfmChecksInProgress(): boolean {
    return this.dfmState.inProgress;
  }

  get hasFailedDfmChecks(): boolean {
    return this.dfmState.failed;
  }

  get cadWarnings(): Array<{ cadObjectName: string; checkLabels: string[] }> {
    return this.dfmState.warnings;
  }

  toggleExpanded(event: Event): void {
    event.stopPropagation();
    this.expanded = !this.expanded;
  }

  openDetails(
    panel:
      | 'details'
      | 'properties'
      | 'dfm'
      | 'pricing'
      | 'drawing'
      | 'capex'
      | 'consumables' = 'details',
  ): void {
    this.detailsPanel = panel;
    this.detailsVisible = true;
  }

  onSelectionChange(selected: boolean): void {
    if (this.readOnly) {
      return;
    }
    this.itemSelectionChange.emit({
      persistentId: this.item.persistent_id,
      selected,
    });
  }

  onSelectionHitAreaClick(event: MouseEvent): void {
    event.stopPropagation();
    if ((event.target as HTMLElement).closest('.p-checkbox')) {
      return;
    }
    this.onSelectionChange(
      !this.selectedPersistentIds.has(this.item.persistent_id),
    );
  }

  canAcceptChildDrop(dragging: QuoteRevisionItem | false): boolean {
    return (
      !!dragging &&
      dragging.persistent_id !== this.item.persistent_id &&
      !this.isDescendantOf(this.item, dragging)
    );
  }

  onDropAsChild(): void {
    if (this.readOnly) {
      return;
    }
    void this.revisionItemService.moveItem(this.item.persistent_id, 'child');
  }

  onDropBefore(childPersistentId: string): void {
    if (this.readOnly) {
      return;
    }
    void this.revisionItemService.moveItem(childPersistentId, 'before');
  }

  onDropAtEnd(): void {
    if (this.readOnly) {
      return;
    }
    void this.revisionItemService.moveItem(this.item.persistent_id, 'end');
  }

  private isDescendantOf(
    item: QuoteRevisionItem,
    ancestor: QuoteRevisionItem,
  ): boolean {
    for (const child of ancestor.children ?? []) {
      if (child.persistent_id === item.persistent_id) {
        return true;
      }
      if (this.isDescendantOf(item, child)) {
        return true;
      }
    }
    return false;
  }

  confirmDelete(event: Event, menu: Popover): void {
    if (this.readOnly) {
      return;
    }
    event.stopPropagation();
    event.preventDefault();
    this.confirmationService.confirm({
      target: event.currentTarget as EventTarget,
      message: 'Are you sure you want to delete this item?',
      icon: 'pi pi-exclamation-triangle',
      acceptButtonProps: { severity: 'danger' },
      rejectButtonProps: { severity: 'secondary', outlined: true },
      accept: () => {
        void this.applyItemAction('delete_item');
        menu.hide();
      },
    });
  }

  async applyItemAction(action: string): Promise<void> {
    if (this.readOnly) {
      return;
    }
    try {
      switch (action) {
        case 'delete_item':
          await this.revisionItemService.bulkDelete([this.item.persistent_id]);
          break;
        case 'update_pdf_visibility':
          await this.revisionItemService.updateItem(this.item.persistent_id, {
            display_children_in_pdf: !this.item.display_children_in_pdf,
          });
          break;
        case 'create_item':
          this.addItemRequested.emit(this.item.persistent_id);
          break;
        default:
          break;
      }
    } finally {
      this.revisionItemService.dragEnd();
    }
  }

  async downloadCad(event: Event): Promise<void> {
    event.stopPropagation();
    await this.revisionItemService.downloadItemCad(this.item.persistent_id);
  }
}
